import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { API_BASE_URL } from '../../config';
import { formatDateToDDMMYYYY } from '../../utils/dateUtils';
import './BalanceSheet.css';

const FINANCE_NAME = 'M/S SHREE MAHAKAL FINANCE AND LEASING (R)';
const FINANCE_ADDRESS = 'Khajuri, Aland , Kalaburgi';

const DEFAULT_YEAR_FROM = '2025-04-01';
const DEFAULT_YEAR_TO = '2026-03-31';

const EXPENSE_HEADER_ORDER = [
  'Office Expenditure A/C',
  'POOJA A/C',
  'PRITING & STATIONRY A/C',
  'FURNITURE A/C',
  'TELE PHONE A/C',
  'ROOM RENT A/C',
  'BULDING DEP A/C',
  'MISC A/C',
  'LEGAL ADVISOR & ADIT F',
  'MEETING EXP A/C',
  'SECURITY DEP A/C',
  'NSC',
  ' LICENCE FEES A/C',
  'LICENCE FEES A/C',
  'COMPUTER A/C',
  'STAMP DUTY A/C',
  'ELECTRICAL EXP A/C',
  'NEWS PAPER & CABLE A/C',
  'Interest On Capital',
  'M.G.PARTNER SALARI A/C',
  'M.G.PARTNER SALARY A/C',
  'PARTNER SALARIES A/C',
  'STAFF SALARY A/C',
  'Intrest Paid On Deposit A/c',
  'NETT LOSS A/C',
  'Professional Tax',
  'Income Tax',
  'BANK CHARGES',
  'Document Charges A/C',
  'CC TV',
  'ACCOUNT SOFTWARE',
  'MD SALARY',
  'BATTA',
  'SOFTWARE A/C',
  'GOLD PURCHASE',
  'PARTNERS INCENTIVE',
  'Tea Expenditure',
  'B Expenditure',
  'Donation',
  'IT Return File',
];

const formatAmount = (value) =>
  Number(value || 0).toLocaleString('en-IN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const escapeHtml = (value) =>
  String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

const BS_PRINT_STYLES = `
  @page { size: A4 portrait; margin: 14mm 10mm 16mm; }
  * { box-sizing: border-box; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  html, body { margin: 0; padding: 0; width: 100%; background: #fff !important; color: #000; font-family: 'Times New Roman', Times, serif; }
  .bs-print-statement { width: 100%; background: #fff; }
  .bs-header { text-align: center; margin-bottom: 18px; padding-bottom: 12px; border-bottom: 1px solid #333; }
  .bs-header-top { display: flex; justify-content: space-between; align-items: center; font-size: 11pt; margin-bottom: 8px; }
  .bs-page-info { font-family: 'Courier New', Courier, monospace; font-size: 10pt; }
  .bs-company-name { margin: 0; color: #8b0000; font-size: 18pt; font-weight: 800; line-height: 1.25; }
  .bs-company-address { margin: 5px 0 10px; font-size: 11pt; }
  .bs-report-title { margin: 0; font-size: 12pt; font-weight: 700; text-transform: uppercase; letter-spacing: 0.4px; }
  .bs-table { width: 100%; border-collapse: collapse; font-size: 11pt; background: #fff; margin-bottom: 20px; }
  .bs-table th, .bs-table td { border: 1px solid #222; padding: 8px 10px; vertical-align: top; background: #fff; }
  .bs-side-heading { text-align: center; font-weight: 700; text-transform: uppercase; background: #fff; }
  .bs-amount-col { width: 140px; text-align: right; white-space: nowrap; font-family: 'Courier New', Courier, monospace; }
  .bs-total-row td { font-weight: 700; border-top: 2px solid #222; }
  .bs-sub-row td:first-child { padding-left: 24px; }
  .bs-section-title { margin: 18px 0 8px; font-size: 12pt; font-weight: 700; text-transform: uppercase; }
`;

const BalanceSheet = () => {
  const [dateFrom, setDateFrom] = useState(DEFAULT_YEAR_FROM);
  const [dateTo, setDateTo] = useState(DEFAULT_YEAR_TO);
  const [balanceData, setBalanceData] = useState(null);
  const [expenses, setExpenses] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const fetchData = useCallback(async () => {
    if (!dateFrom || !dateTo) {
      setError('Please select both from and to dates.');
      return;
    }
    if (new Date(dateFrom) > new Date(dateTo)) {
      setError('From date cannot be greater than To date.');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      const [balanceResponse, expenseResponse] = await Promise.all([
        fetch(`${API_BASE_URL}/balance-enquiry?date_from=${dateFrom}&date_to=${dateTo}`),
        fetch(`${API_BASE_URL}/expense-manager`),
      ]);

      if (!balanceResponse.ok) {
        const payload = await balanceResponse.json().catch(() => ({}));
        throw new Error(payload.message || 'Failed to fetch balance enquiry data.');
      }
      if (!expenseResponse.ok) {
        const payload = await expenseResponse.json().catch(() => ({}));
        throw new Error(payload.message || 'Failed to fetch expense data.');
      }

      const balanceJson = await balanceResponse.json();
      const expenseJson = await expenseResponse.json();

      setBalanceData(balanceJson);
      setExpenses(expenseJson.expenses || []);
    } catch (err) {
      console.error('Balance sheet fetch failed:', err);
      setError(err.message || 'Unable to load balance sheet data.');
      setBalanceData(null);
      setExpenses([]);
    } finally {
      setIsLoading(false);
    }
  }, [dateFrom, dateTo]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const expenditureRows = useMemo(() => {
    const fromDate = new Date(dateFrom);
    const toDate = new Date(dateTo);
    const totalsByHeader = {};

    expenses.forEach((expense) => {
      const expenseDate = new Date(expense.date);
      if (expenseDate < fromDate || expenseDate > toDate) return;

      const header = String(expense.headerName || '').trim();
      if (!header) return;

      totalsByHeader[header] = (totalsByHeader[header] || 0) + Number(expense.amount || 0);
    });

    const knownHeaders = EXPENSE_HEADER_ORDER.filter((header) => totalsByHeader[header] > 0);
    const extraHeaders = Object.keys(totalsByHeader)
      .filter((header) => !EXPENSE_HEADER_ORDER.includes(header))
      .sort((a, b) => a.localeCompare(b));

    return [...knownHeaders, ...extraHeaders].map((header) => ({
      label: header,
      amount: totalsByHeader[header],
    }));
  }, [expenses, dateFrom, dateTo]);

  const glInterest = Number(balanceData?.generalLoanInterest || 0);
  const cdInterest = Number(balanceData?.cdLoanInterest || 0);
  const documentCharges = Number(balanceData?.documentChargesCredit || 0);
  const totalIncome = glInterest + cdInterest + documentCharges;
  const totalExpenditure = expenditureRows.reduce((sum, row) => sum + row.amount, 0);
  const netIncome = totalIncome > totalExpenditure ? totalIncome - totalExpenditure : 0;

  const capitalAmount = 1000000;
  const sbAccount = 2850000;
  const glOutstanding =
    Number(balanceData?.generalLoanDebit || 0) - Number(balanceData?.generalLoanEMI || 0);
  const cdOutstanding = Number(balanceData?.cdLoanDebit || 0) - Number(balanceData?.cdLoanEMI || 0);
  const outstandingLoan = glOutstanding + cdOutstanding;
  const overallBalance = Number(balanceData?.overallBalance || 0);
  // Exclude overall balance from assets when it is negative
  const overallBalanceForAssets = overallBalance > 0 ? overallBalance : 0;

  const liabilitiesTotal = capitalAmount + netIncome + sbAccount;
  const assetsTotal = outstandingLoan + overallBalanceForAssets;

  const periodHeading = `BALANCE SHEET AS ON ${formatDateToDDMMYYYY(dateTo)}`;
  const periodNote = `(For the period ${formatDateToDDMMYYYY(dateFrom)} to ${formatDateToDDMMYYYY(dateTo)})`;

  const liabilityRows = useMemo(
    () => [
      { label: 'Capital Amount', amount: capitalAmount },
      { label: 'Net Income', amount: netIncome },
      { label: 'SB A/C', amount: sbAccount },
    ],
    [capitalAmount, netIncome, sbAccount]
  );

  const assetRows = useMemo(() => {
    const rows = [
      { label: 'General Loan', amount: glOutstanding },
      { label: 'CD Loan', amount: cdOutstanding },
    ];
    if (overallBalance > 0) {
      rows.push({
        label: `Overall Balance as on ${formatDateToDDMMYYYY(dateTo)}`,
        amount: overallBalance,
      });
    }
    return rows;
  }, [glOutstanding, cdOutstanding, overallBalance, dateTo]);

  const bodyRowCount = Math.max(liabilityRows.length, assetRows.length);

  const handlePrint = () => {
    const printWindow = window.open('', '_blank', 'width=1100,height=800');
    if (!printWindow) {
      alert('Please allow popups to print the balance sheet.');
      return;
    }

    const mainTableRows = Array.from({ length: bodyRowCount })
      .map((_, index) => {
        const left = liabilityRows[index];
        const right = assetRows[index];
        const rightClass = right?.isSub ? 'bs-sub-row' : '';
        return `
          <tr>
            <td>${escapeHtml(left?.label || '')}</td>
            <td class="bs-amount-col">${left ? escapeHtml(formatAmount(left.amount)) : ''}</td>
            <td class="${rightClass}">${escapeHtml(right?.label || '')}</td>
            <td class="bs-amount-col">${right ? escapeHtml(formatAmount(right.amount)) : ''}</td>
          </tr>
        `;
      })
      .join('');

    const expenseRowsHtml = expenditureRows
      .map(
        (row) => `
        <tr>
          <td>${escapeHtml(row.label)}</td>
          <td class="bs-amount-col">${escapeHtml(formatAmount(row.amount))}</td>
        </tr>
      `
      )
      .join('');

    const printHtml = `
      <!doctype html>
      <html>
        <head>
          <meta charset="utf-8" />
          <title>Balance Sheet - ${escapeHtml(formatDateToDDMMYYYY(dateTo))}</title>
          <style>${BS_PRINT_STYLES}</style>
        </head>
        <body>
          <div class="bs-print-statement">
            <div class="bs-header">
              <div class="bs-header-top">
                <span></span>
                <span class="bs-page-info">Page 1 of 1</span>
              </div>
              <h1 class="bs-company-name">${escapeHtml(FINANCE_NAME)}</h1>
              <p class="bs-company-address">${escapeHtml(FINANCE_ADDRESS)}</p>
              <h2 class="bs-report-title">${escapeHtml(periodHeading)}</h2>
              <p>${escapeHtml(periodNote)}</p>
            </div>
            <table class="bs-table">
              <thead>
                <tr>
                  <th colspan="2" class="bs-side-heading">Liabilities</th>
                  <th colspan="2" class="bs-side-heading">Current Assets</th>
                </tr>
                <tr>
                  <th>Particulars</th>
                  <th class="bs-amount-col">Amount</th>
                  <th>Particulars</th>
                  <th class="bs-amount-col">Amount</th>
                </tr>
              </thead>
              <tbody>
                ${mainTableRows}
                <tr class="bs-total-row">
                  <td><strong>Total</strong></td>
                  <td class="bs-amount-col"><strong>${escapeHtml(formatAmount(liabilitiesTotal))}</strong></td>
                  <td><strong>Total</strong></td>
                  <td class="bs-amount-col"><strong>${escapeHtml(formatAmount(assetsTotal))}</strong></td>
                </tr>
              </tbody>
            </table>
            <h3 class="bs-section-title">Depreciation &amp; Expense (till ${escapeHtml(formatDateToDDMMYYYY(dateTo))})</h3>
            <table class="bs-table">
              <thead>
                <tr>
                  <th>Particulars</th>
                  <th class="bs-amount-col">Amount</th>
                </tr>
              </thead>
              <tbody>
                ${expenseRowsHtml}
                <tr class="bs-total-row">
                  <td><strong>Total</strong></td>
                  <td class="bs-amount-col"><strong>${escapeHtml(formatAmount(totalExpenditure))}</strong></td>
                </tr>
              </tbody>
            </table>
          </div>
          <script>
            window.onload = function() {
              window.focus();
              window.print();
              window.onafterprint = function() { window.close(); };
            };
          <\/script>
        </body>
      </html>
    `;

    printWindow.document.open();
    printWindow.document.write(printHtml);
    printWindow.document.close();
  };

  return (
    <div className="bs-container">
      <div className="bs-toolbar no-print">
        <div className="bs-toolbar-title">
          <h2>Balance Sheet</h2>
          <p>Financial position statement</p>
        </div>
        <div className="bs-toolbar-actions">
          <div className="bs-filter">
            <div className="filter-group">
              <label htmlFor="bs-from">From</label>
              <input
                id="bs-from"
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
              />
            </div>
            <div className="filter-group">
              <label htmlFor="bs-to">To</label>
              <input
                id="bs-to"
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
              />
            </div>
            <button type="button" className="bs-btn bs-btn-secondary" onClick={fetchData} disabled={isLoading}>
              Refresh
            </button>
            <button type="button" className="bs-btn bs-btn-primary" onClick={handlePrint} disabled={isLoading || !!error}>
              Print
            </button>
          </div>
        </div>
      </div>

      {error ? <div className="bs-error no-print">{error}</div> : null}
      {isLoading ? <div className="bs-loading">Loading balance sheet data…</div> : null}

      {!isLoading && !error ? (
        <div className="bs-statement">
          <div className="bs-header">
            <div className="bs-header-top">
              <span />
              <span className="bs-page-info">Page 1 of 1</span>
            </div>
            <h1 className="bs-company-name">{FINANCE_NAME}</h1>
            <p className="bs-company-address">{FINANCE_ADDRESS}</p>
            <h2 className="bs-report-title">{periodHeading}</h2>
            <p className="bs-period-note">{periodNote}</p>
          </div>

          <div className="bs-table-wrapper">
            <table className="bs-table">
              <thead>
                <tr>
                  <th colSpan="2" className="bs-side-heading">
                    Liabilities
                  </th>
                  <th colSpan="2" className="bs-side-heading">
                    Current Assets
                  </th>
                </tr>
                <tr>
                  <th>Particulars</th>
                  <th className="bs-amount-col">Amount</th>
                  <th>Particulars</th>
                  <th className="bs-amount-col">Amount</th>
                </tr>
              </thead>
              <tbody>
                {Array.from({ length: bodyRowCount }).map((_, index) => {
                  const left = liabilityRows[index];
                  const right = assetRows[index];
                  return (
                    <tr key={`row-${index}`}>
                      <td>{left?.label || ''}</td>
                      <td className="bs-amount-col">{left ? formatAmount(left.amount) : ''}</td>
                      <td className={right?.isSub ? 'bs-sub-label' : ''}>{right?.label || ''}</td>
                      <td className="bs-amount-col">{right ? formatAmount(right.amount) : ''}</td>
                    </tr>
                  );
                })}
                <tr className="bs-total-row">
                  <td>
                    <strong>Total</strong>
                  </td>
                  <td className="bs-amount-col">
                    <strong>{formatAmount(liabilitiesTotal)}</strong>
                  </td>
                  <td>
                    <strong>Total</strong>
                  </td>
                  <td className="bs-amount-col">
                    <strong>{formatAmount(assetsTotal)}</strong>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <h3 className="bs-section-title">Depreciation &amp; Expense (till {formatDateToDDMMYYYY(dateTo)})</h3>
          <div className="bs-table-wrapper">
            <table className="bs-table bs-expense-table">
              <thead>
                <tr>
                  <th>Particulars</th>
                  <th className="bs-amount-col">Amount</th>
                </tr>
              </thead>
              <tbody>
                {expenditureRows.length === 0 ? (
                  <tr>
                    <td colSpan="2" className="bs-empty-row">
                      No expense entries for the selected period.
                    </td>
                  </tr>
                ) : (
                  expenditureRows.map((row) => (
                    <tr key={row.label}>
                      <td>{row.label}</td>
                      <td className="bs-amount-col">{formatAmount(row.amount)}</td>
                    </tr>
                  ))
                )}
                <tr className="bs-total-row">
                  <td>
                    <strong>Total</strong>
                  </td>
                  <td className="bs-amount-col">
                    <strong>{formatAmount(totalExpenditure)}</strong>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default BalanceSheet;
